// Equipment add/edit page.

import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ChipSelector} from '../../components/common/ChipSelector'
import {FormField} from '../../components/common/FormField'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {showToast} from '../../components/common/Toast'
import {supabase, DEFAULT_MINE_ID} from '../../app/supabaseClient'
import {
  EQUIPMENT_ACTIVITIES,
  EQUIPMENT_STATUSES,
  generateId,
  suggestAssetId,
} from '../../lib/constants'
import type {EquipmentDetail, EquipmentStatus} from './types'

type EquipmentActivity = (typeof EQUIPMENT_ACTIVITIES)[number]

interface FormErrors {
  name?: string
  model?: string
  asset_id?: string
  activity?: string
}

export const EquipmentFormScreen: React.FC = () => {
  const {id} = useParams<{id?: string}>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const [name, setName] = useState('')
  const [model, setModel] = useState('')
  const [assetId, setAssetId] = useState('')
  const [activity, setActivity] = useState<EquipmentActivity | null>(null)
  const [status, setStatus] = useState<EquipmentStatus>('active')

  useEffect(() => {
    if (!isEdit || !id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data, error} = await supabase
        .from('equipment')
        .select('id, name, model, asset_id, activity, status')
        .eq('id', id)
        .single<EquipmentDetail>()
      if (cancelled) return
      if (error || !data) {
        showToast(`Load failed: ${error?.message ?? 'Not found'}`, 'error')
        navigate('/equipment')
        return
      }
      setName(data.name)
      setModel(data.model)
      setAssetId(data.asset_id)
      setActivity((data.activity as EquipmentActivity | null) ?? null)
      setStatus(data.status)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isEdit, navigate])

  useEffect(() => {
    if (isEdit) return
    setActivity(EQUIPMENT_ACTIVITIES[0])
  }, [isEdit])

  const validate = (): boolean => {
    const e: FormErrors = {}
    if (!name.trim()) e.name = 'OEM / Name is required'
    if (!model.trim()) e.model = 'Model is required'
    if (isEdit && !assetId.trim()) e.asset_id = 'Asset ID is required'
    if (!activity) e.activity = 'Pick an activity'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setBusy(true)
    const now = new Date().toISOString()
    if (!isEdit) {
      const newId = generateId()
      const aid = assetId.trim() || suggestAssetId()
      const {error} = await supabase.from('equipment').insert({
        id: newId,
        mine_id: DEFAULT_MINE_ID,
        name: name.trim(),
        model: model.trim(),
        asset_id: aid,
        activity,
        status,
        created_at: now,
        updated_at: now,
      })
      if (error) {
        showToast(`Add failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Equipment added.', 'success')
    } else {
      const {error} = await supabase
        .from('equipment')
        .update({
          name: name.trim(),
          model: model.trim(),
          asset_id: assetId.trim(),
          activity,
          status,
          updated_at: now,
        })
        .eq('id', id!)
      if (error) {
        showToast(`Update failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Equipment updated.', 'success')
    }
    setBusy(false)
    navigate('/equipment')
  }

  const handleDelete = async () => {
    if (!id) return
    if (!window.confirm(`Delete ${name} ${model} (${assetId})?`)) return
    setBusy(true)
    await supabase
      .from('tags')
      .update({assigned_to_id: null, assigned_to_type: null})
      .eq('assigned_to_id', id)
      .eq('assigned_to_type', 'equipment')
    const {error} = await supabase.from('equipment').delete().eq('id', id)
    setBusy(false)
    if (error) {
      showToast(`Delete failed: ${error.message}`, 'error')
      return
    }
    showToast('Equipment deleted.', 'success')
    navigate('/equipment')
  }

  const title = isEdit ? `Edit ${assetId || 'Equipment'}` : 'Add Equipment'
  const saveLabel = isEdit ? 'Save Changes' : 'Add Equipment'

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={title} backTo="/equipment" />
      <PageShell
        footer={
          <>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="mr-auto rounded-md border border-statusRed px-lg py-sm text-md text-statusRed hover:bg-statusRed hover:text-white disabled:opacity-40"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/equipment')}
              disabled={busy}
              className="rounded-md border border-surfaceBorder px-lg py-sm text-md text-textSecondary hover:border-textPrimary hover:text-textPrimary disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-md bg-primary px-lg py-sm text-md font-bold text-black hover:bg-primaryLight disabled:opacity-40"
            >
              {busy ? 'Saving…' : saveLabel}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : (
          <div className="flex flex-col gap-lg">
            <div className="flex gap-lg">
              <FormField
                label="OEM / Name"
                value={name}
                onChange={setName}
                placeholder="e.g. Komatsu"
                error={errors.name ?? null}
              />
              <FormField
                label="Model"
                value={model}
                onChange={setModel}
                placeholder="e.g. Truck"
                error={errors.model ?? null}
              />
            </div>
            <FormField
              label="Asset ID"
              value={assetId}
              onChange={setAssetId}
              placeholder="EQ_xxx or model-specific code (auto-generated if empty)"
              error={errors.asset_id ?? null}
            />

            <ChipSelector
              label="Activity"
              options={EQUIPMENT_ACTIVITIES}
              value={activity}
              onChange={setActivity}
              error={errors.activity ?? null}
            />

            {isEdit && (
              <ChipSelector
                label="Status"
                options={EQUIPMENT_STATUSES}
                value={status}
                onChange={setStatus}
              />
            )}
          </div>
        )}
      </PageShell>
    </main>
  )
}
